import React from "react";
import styled from "styled-components";
import { FiGithub, FiPackage, FiAward } from "react-icons/fi";
import { SiKaggle } from "react-icons/si";

// Bullet list with inline icon links, shared by the Experience cards and the
// Proving Ground block. Markers are hard squares, not round dots — dell-1996
// has no soft shapes, and they match the window-chrome markers.

export const Points = styled.ul`
  list-style: none;
  margin: ${({ $bare }) => ($bare ? "0" : "12px 0 0")};
  padding-top: ${({ $bare }) => ($bare ? "0" : "12px")};
  padding-left: 0;
  border-top: ${({ $bare }) => ($bare ? "none" : "1px solid rgba(0, 0, 0, 0.18)")};
`;

const Point = styled.li`
  position: relative;
  padding-left: 16px;
  margin-bottom: 10px;

  font-size: 13.5px;
  line-height: 1.6;
  color: ${({ theme }) => theme.biome.experience.muted};
  text-align: justify;
  hyphens: auto;

  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 8px;
    width: 6px;
    height: 6px;
    background: ${({ theme }) => theme.biome.experience.accent};
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const SubPoints = styled.ul`
  list-style: none;
  margin: 8px 0 0;
  padding-left: 4px;
`;

// Hollow marker against the solid one, so nesting reads without relying on
// indentation alone.
const SubPoint = styled(Point)`
  font-size: 13px;
  margin-bottom: 6px;

  &::before {
    width: 5px;
    height: 5px;
    background: transparent;
    border: 1px solid ${({ theme }) => theme.biome.experience.accent};
  }
`;

const PinLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  vertical-align: -2px;
  margin-left: 6px;
  width: 18px;
  height: 18px;
  border-radius: 0;
  border: 1px solid rgba(0, 0, 0, 0.3);
  color: ${({ theme }) => theme.biome.experience.ink};
  font-size: 11px;
  transition: background 120ms linear, color 120ms linear;

  &:hover {
    background: ${({ theme }) => theme.biome.experience.accent};
    border-color: ${({ theme }) => theme.biome.experience.accent};
    color: #f8eee1;
  }
`;

// `apify` uses a package glyph rather than a redrawn brand mark — approximating
// someone's trademark badly is worse than an honest generic icon.
// `certificate` hrefs are imported PNGs, so the link opens the raw image.
const LINK_ICONS = {
  github: FiGithub,
  apify: FiPackage,
  certificate: FiAward,
  kaggle: SiKaggle,
};

const LINK_LABELS = {
  github: "View on GitHub",
  apify: "View on Apify",
  certificate: "View certificate",
  kaggle: "View Kaggle leaderboard",
};

export const Pins = ({ links }) =>
  links?.map((l) => {
    const Icon = LINK_ICONS[l.kind] || FiGithub;
    const label = LINK_LABELS[l.kind] || "Open link";
    return (
      <PinLink
        key={l.href}
        href={l.href}
        target="_blank"
        rel="noreferrer"
        aria-label={label}
        title={label}
      >
        <Icon />
      </PinLink>
    );
  });

/**
 * @param points  [{ text, links?, sub? }]
 * @param bare    drop the top rule and padding (used where a label already separates)
 */
const PointList = ({ points, bare = false }) => {
  if (!points?.length) return null;

  return (
    <Points $bare={bare}>
      {points.map((pt) => (
        <Point key={pt.text}>
          {pt.text}
          <Pins links={pt.links} />

          {pt.sub?.length > 0 && (
            <SubPoints>
              {pt.sub.map((s) => (
                <SubPoint key={s.text}>
                  {s.text}
                  <Pins links={s.links} />
                </SubPoint>
              ))}
            </SubPoints>
          )}
        </Point>
      ))}
    </Points>
  );
};

export default PointList;
